<template>
  <v-hover v-slot="{ hover }">
    <v-autocomplete v-model="modelValue" :search-input="searchValue" @update:search-input="onSearchInput" :items="list"
      :filter="i => i" :readonly="!externaledit && (IsDisabled || !edit)" ref="QPaginatedSearchDropdown" class="q-input"
      :class="[BorderClass, CompactClass, BlurrClass, InputColorClass, { 'focus-empty': isFocused }]"
      :outlined="outlined" :background-color="BackgroundColor" :label="labelText" hide-details="auto"
      :disabled="IsDisabled" :single-line="singleline" :color="color" :item-text="textkey" :item-value="valuekey"
      :return-object="returnobject" :multiple="multiple" :small-chips="multiple" 
      :clearable="!!externaledit && !IsEmpty && clearable" flat :dense="dense" :hoverbool="setHoverBool(hover)"
      :autofocus="autofocus" :menu-props="menuProps" :menu.sync="menuOpen" :open-on-click="true" :open-on-focus="true"
      :attach="!disableAttach"
      :placeholder="disabled ? $t('servicesystem.common_objects.commonterms.not_selectable') : placeholderText"
      persistent-placeholder :rules="autoNoEmptyRules" :loading="loading || nextPageLoading"
      @click:clear="$emit('clear')" @keypress="editKeyPress" @keydown.native.capture="onKeydownCapture"
      @keydown="keyDown" @click="open()" @focus="f => { setEdit(f); open(); }" @blur="b => { blurInput(b); if (!isCreating) close(); }"
      @change="onChange">
      <!--  @mouseover="setHoverBool(true)" @mouseleave="setHoverBool(false)" -->

      <!-- PREPEND SLOTS -->
      <template v-slot:prepend>
        <slot name="prepend-outer" />
      </template>

      <template v-slot:prepend-inner>
        <slot name="prepend" />
      </template>
      <!------------------->

      <!-- NO DATA TEMPLATE -->

      <template v-slot:no-data>
        <p v-if="!allowCreate" class="ma-0 px-3 py-2" style="background-color: #f2f2f2;">{{ noDataText }}</p>
      </template>
      <!--------------------------->

      <!-- SELECTION CHIPS TEMPLATE -->

      <template v-slot:selection="data">
        <slot name="selection" :data="data" :item="data.item">
          <v-chip v-if="multiple" :key="refreshViewTrigger + '' + JSON.stringify(data.item)"
            :color="data.item.Color ? data.item.Color : (color ? color : 'primary')" :disabled="IsDisabled"
            tabindex="-1" class="ml-0"
            :style="!externaledit && !edit ? 'margin-top: 2px; margin-bottom: 2px;' : 'margin-top: 2px; margin-bottom: 2px;'"
            style="max-width: 100%;" dark small @click="data.select">
            <span class="text-truncate">{{ itemText(data.item) }}</span>
            <v-btn v-if="!IsDisabled && (clearable || (multiple && (newValue.length > 1)))" icon x-small tabindex="-1"
              style="margin-right: -10px !important;" @click="remove(data.item)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-chip>
          <div v-else :class="InputColor + '--text'">{{ itemText(data.item) }}</div>
        </slot>
      </template>
      <!--------------------------->

      <!-- PREPEND DROPDOWN ITEM TEMPLATE -->
      <template #prepend-item>
        <slot name="prepend-item">
          <!-- 
          <div v-if="!searchLoading && (searchValue && searchValue.length >= minSearchCharLimit)" class="px-2 py-1"
            style="position: sticky; top: 0; margin-right: 0px; z-index: 20; display: inline-flex; width: auto; background-color: rgba(255, 255, 255, 0.871);">
            {{ totalCount }} results
          </div>
          -->
          <template v-if="multiple && list && list.length > 2 && ((itemsPerPage / 2) >= list.length) && !searchValue">
            <v-list-item @click="toggleAll()" style="background-color: #f2f2f2 !important">
              <v-list-item-action>
                <v-checkbox :input-value="(modelValue && list && (list.length === modelValue.length))"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('servicesystem.common_objects.commonterms.all') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </slot>
      </template>
      <!----------------------------------->

      <!-- DROPDOWN ITEM TEMPLATE --------->
      <template v-slot:item="data">
        <slot name="item" :item="data.item" :on="data.on" :attrs="data.attrs">
          <!-- selected checkbox on multiple -->
          <v-list-item-action v-if="multiple" class="ma-0 ml-n2">
            <v-simple-checkbox v-on="data.on" :ripple="false" :value="data.attrs.inputValue" class="mr-2">
            </v-simple-checkbox>
          </v-list-item-action>
          <v-list-item-content class="py-0">
            <slot name="item-content" :item="data.item" :on="data.on" :attrs="data.attrs" :highlight="highlightMatch"
              :search="searchValue">
              <v-list-item-title v-html="highlightMatch(itemText(data.item))">
              </v-list-item-title>
            </slot>
          </v-list-item-content>
          <!-- selected indicator on single -->
          <v-list-item-icon v-if="!multiple && data.attrs.inputValue" class="my-auto ml-0">
            <v-icon size="18">mdi-check</v-icon>
          </v-list-item-icon>
        </slot>
      </template>
      <!----------------------------------->

      <!-- APPEND DROPDOWN ITEM TEMPLATE -->
      <template #append-item>
        <slot name="append-item">
        </slot>
        <template v-if="nextPageLoading">
          <v-skeleton-loader v-for="i in 1" :key="i" type="list-item" :style="'opacity:' + (5 - i) / 4 + ';'">
          </v-skeleton-loader>
        </template>
        <template
          v-else-if="searchValue && searchValue != '' && allowCreate && !nextPageLoading && !searchLoading && !valueTextExists(searchValue) && !valueExists(searchValue)">
          <v-divider></v-divider>
          <div class="ma-0 px-3 py-2" @click.stop @mousedown.stop>
            <v-btn x-small color="primary" @click.stop.prevent="create" @mousedown.stop.prevent="setCreating">
              {{ $t("servicesystem.common_objects.commonterms.create") }}: {{ searchValue }}
            </v-btn>
          </div>
        </template>
        <!-- The intersect line is a trigger for loading the next page in paginated dropdowns as you scroll. -->
        <div v-intersect="endIntersect" class="intersect-point-line" :class="isProd || 'visible'"
          :style="'bottom:' + (194) + 'px;'" />

      </template>
      <!----------------------------------->

      <!-- APPEND SLOTS -->
      <template v-slot:append>
        <v-progress-circular v-if="loading" indeterminate size="20" width="2" color="primary">
        </v-progress-circular>
        <LockedWarning v-else-if="disabled" :hover="hover" />
      </template>

      <template v-slot:append-outer>
        <slot name="append-outer" />
      </template>
      <!---------------->

    </v-autocomplete>
  </v-hover>
</template>


<script>
import LockedWarning from "@/components/Inputs/Parts/LockedWarning.vue";
import InputMixin from "@/helpers/Mixins/InputMixin";
import QInputRuleMixin from "@/helpers/Mixins/QInputRuleMixin";
import { debounce } from "debounce";
import SmartMenuPropMixin from "./SmartMenuPropMixin";

export default {
  name: "QPaginatedSearchDropdown",
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    label: String,
    value: [Object, String, Number, Array],
    color: String,
    singleline: Boolean,
    clearable: Boolean,
    disableAttach: Boolean, //can be disabled if the input is too small to unlock a wider dropdown. Recomended to keep on to make dropdown and input width match.
    disabled: Boolean,
    loading: Boolean,
    outlined: Boolean,
    dense: Boolean,
    externaledit: Boolean,
    emitOnlyOnBlur: Boolean,
    placeholder: [String, Object],
    rules: Array,

    listGetter: {
      type: Function,
      required: true
    },
    filter: Object,
    itemsPerPage: {
      type: Number,
      default: 100,
    },
    minSearchCharLimit: {
      type: Number,
      default: 3,
    },
    textkey: String,
    valuekey: String,
    returnobject: Boolean,
    multiple: Boolean,
    allowCreate: Boolean,
    autofocus: Boolean,
    resetSearchOnClose: {
      type: Boolean,
      default: true
    },
  },
  components: {
    LockedWarning,
  },
  mixins: [InputMixin, QInputRuleMixin, SmartMenuPropMixin],

  data() {
    return {
      edit: false,
      newValue: this.value,
      searchValue: "",
      list: [],
      page: 1,
      totalCount: 0,
      nextPageLoading: false,
      searchLoading: false,
      refreshViewTrigger: 1,
      cancelTokenContollerList: [],
      menuOpen: false,
      isFocused: false,
      isCreating: false,
    };
  },

  setup(props, { slots }) {
    const hasSlot = name => !!slots[name]
    return { hasSlot }
  },

  mounted() {
    this.getNextItems();
  },

  beforeDestroy() {
    this.cancelAllOpenRequests();
  },

  methods: {
    cancelAllOpenRequests() {
      //console.log('cancelling all open requests');
      for (const controller of this.cancelTokenContollerList) {
        controller.abort();
      }
      this.cancelTokenContollerList = [];
    },

    setCreating(e) {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      this.isCreating = true;
    },
    create(e) {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      //console.log('create clicked')
      this.isCreating = true;
      this.$emit('create', { value: this.searchValue, onComplete: this.createComplete });
      // Reset flag after a short delay to allow event to propagate
      this.$nextTick(() => {
        setTimeout(() => {
          this.isCreating = false;
        }, 100);
      });
    },
    async createComplete() {
      await this.getNextItems();
      this.$refs.QPaginatedSearchDropdown.blur();
    },
    keyDown(args) {
      if (args.key.length == 1) {
        this.nextPageLoading = true;
      }
      this.editKeyDown(args);
    },
    itemText(item) {
      return this.textkey ? (typeof item === 'object' ? item[this.textkey] : item) : item;
    },

    itemValue(item) {
      if (item == null) return null;
      return this.valuekey && !this.returnobject ? (typeof item === 'object' ? item[this.valuekey] : item) : item;
    },

    remove(item) {
      const samefunc = (x, y) => this.SameValue(x, y);
      const valuegetter = (z) => this.itemValue(z);
      if (this.multiple) {
        this.newValue = this.newValue.filter(function (x) {
          return !samefunc(x, valuegetter(item));
        });
      }
      else {
        this.newValue = null;
      }
    },

    toggleAll() {
      if (this.list.length === this.modelValue.length) {
        this.modelValue = [];
      } else {
        if (this.valuekey && !this.returnobject) {
          this.modelValue = this.list.map(x => x.Id)
        } else {
          this.modelValue = Object.assign([], this.list);
        }
      }
    },

    clearResults() {
      //console.log('clearing');
      if (this.multiple && this.newValue) {
        // Preserve selected items as objects so chips keep their labels
        const selectedValues = new Set((Array.isArray(this.newValue) ? this.newValue : [])
          .map(v => this.itemValue(v))
          .filter(v => v != null));
        this.list = this.list
          .filter(item => item != null && selectedValues.has(this.itemValue(item)));
      } else if (this.newValue) {
        if (typeof this.newValue === 'object') {
          this.list = [this.newValue];
        }
        else {
          this.list = this.list.filter(y => this.itemValue(y) == this.itemValue(this.newValue))
        }
      }
      else {
        this.list = []
      }
      this.totalCount = 0;
      this.page = 1;
    },

    debounceSearchInput: debounce(async function (keyword) {
      if (this.newValue && keyword == this.itemText(this.newValue)) {
        //console.log('killed debounce');
        return;
      }
      if (!this.minSearchCharLimit || (keyword && keyword.length >= this.minSearchCharLimit)) {
        this.cancelAllOpenRequests();
        this.searchLoading = true;
        await this.getNextItems();
        this.searchLoading = false;
      }
    }, 700),

    endIntersect(entries, observer, isIntersecting) {
      if (isIntersecting && !this.nextPageLoading) {
        this.page++;
        this.getNextItems();
      }
    },

    async getNextItems() {
      const controller = new AbortController();
      const cancelationToken = controller.signal;
      this.cancelTokenContollerList.push(controller);
      var prependItems = this.missingValues;
      //console.log('prependItems', prependItems)
      this.nextPageLoading = true;
      var filterDto = Object.assign({}, this.filter)
      this.page = 1;
      filterDto.SearchWord = this.searchValue ?? '';
      filterDto.ItemsPerPage = this.itemsPerPage;
      filterDto.Page = this.page;
      filterDto.PrependItemIdList = prependItems;

      var resultWrapper = await this.listGetter(filterDto, cancelationToken);
      var pageItems;
      if (resultWrapper.Content) {
        pageItems = resultWrapper.Content;
        this.totalCount = resultWrapper.TotalCount;
      }
      else {
        this.totalCount = 0;
        pageItems = resultWrapper;
      }

      if (this.searchValue) this.clearResults();
      this.list = [...this.list, ...pageItems];

      this.nextPageLoading = false;
    },

    valueExists(val) {
      return this.list.some(y => this.itemValue(y) == this.itemValue(val))
    },
    valueTextExists(val) {
      if (this.list.length == 0 || !val) return false;
      return this.list.some(y => this.itemText(y) == this.itemText(val))
    },

    open() {
      if (!this.IsDisabled) {
        this.isFocused = true;
        this.menuOpen = true;
      }
    },
    close() {
      // Don't close if we're in the middle of creating
      if (this.isCreating) {
        return;
      }
      //console.log('close')
      this.isFocused = false;
      this.menuOpen = false;

      // Always reset search state on close for single-select to avoid persisting invisible queries
      if (!this.multiple && this.resetSearchOnClose) {
        if (this.searchValue) {
          this.searchValue = "";
        }
        // Clear current results and fetch default list (unfiltered)
        this.clearResults();
        this.getNextItems();
      }
    },

    onChange(val) {
      this.$emit('change', val);

      if (!this.multiple) {
        this.close();
      }
    },

    onSearchInput(val) {
      var newVal = (val === undefined || val === null) ? '' : val;
      if (newVal === this.searchValue) return;
      this.searchValue = newVal;
      this.debounceSearchInput(this.searchValue);
    },

    // Highlight helpers
    escapeRegExp(str) {
      return String(str ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    escapeHtml(unsafe) {
      const s = String(unsafe ?? '');
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    highlightMatch(text) {
      const source = String(text ?? '');
      const re = this.highlightRegex;
      if (!re) return this.escapeHtml(source);

      try {
        // Reset lastIndex because we reuse the same regex instance
        re.lastIndex = 0;
        const parts = source.split(re);
        re.lastIndex = 0;
        const matches = source.match(re) || [];
        let html = '';
        for (let i = 0; i < parts.length; i++) {
          html += this.escapeHtml(parts[i]);
          if (i < matches.length) {
            html += `<mark class="qpsd-highlight">` + this.escapeHtml(matches[i]) + `</mark>`;
          }
        }
        return html;
      } catch (e) {
        return this.escapeHtml(source);
      }
    },

    onKeydownCapture(e) {
      if (this.multiple) return;

      if (e.key === 'Backspace') {
        // Let the browser delete the character,
        // but prevent Vuetify from seeing this keydown and clearing the value.
        e.stopPropagation()

        // If already empty, also prevent default to avoid any weirdness
        // (like navigating back when not focused in an input).
        if (!this.searchValue || this.searchValue.length < 2) {
          //console.log('blocked backspace');
          if (this.searchValue) {
            this.searchValue = "";
          }
          e.preventDefault()
        }

        // keep menu open while editing search
        this.open();
      }
    },
  },

  computed: {
    noDataText() {
      if (this.minSearchCharLimit && (!this.searchValue || (this.searchValue.length < this.minSearchCharLimit))) {
        return this.$t('servicesystem.common_objects.commonterms.type_three_chars_to search')
      }
      else if (this.searchLoading) {
        return this.$t('servicesystem.common_objects.commonterms.searching')
      }
      else {
        return this.$t('servicesystem.common_objects.commonterms.no_results')
      }
    },

    isProd() {
      return (process.env.NODE_ENV === 'production');
    },

    // Precompile regex for highlighting to minimize per-item work
    highlightRegex() {
      const q = String(this.searchValue ?? '').trim();
      if (!q) return null;
      try {
        return new RegExp(this.escapeRegExp(q), 'ig');
      } catch {
        return null;
      }
    },

    missingValues() {
      var list = [];
      var val = this.isFocused ? this.value : this.modelValue;
      if (val) {
        if (this.multiple && val[0]) {
          //console.log('A')
          list = val.filter((x) => (!this.valueExists(x)))
        }
        else if (!this.valueExists(val)) {
          //console.log('B')
          list.push(val)
        }

        if (list.length) {
          if (!this.returnobject) {
            //console.log('C')
            list = list.map(z => this.itemValue(z));
          }
          else if (this.valuekey) {
            //console.log('D')
            list = list.map(z => z[this.valuekey]);
          }
        }

        list = list.filter(x => x != null);
        //console.log('val', val)
        //console.log('list', list, this.list)
        //console.log('valueExists', this.valueExists(val))
      }
      return list;
    },

    autoNoEmptyRules() {
      var r = Object.assign([], this.rules);
      if (!this.clearable && !r?.some(x => x.name == 'requiredRule')) {
        r = [...r, this.requiredRule];
      }
      //console.log(r);
      return r;
    },

    IsRequired() { //ovverides default from mixin to handle the autoNoEmptyRules
      return (this.autoNoEmptyRules && this.autoNoEmptyRules.some(x => x.name == 'requiredRule'));
    },
  },

  watch: {
    filter: {
      handler() {
        this.clearResults();
        this.debounceSearchInput(this.searchValue);
      },
      deep: true
    },

    value: function (newVal, oldVal) {
      if (newVal != oldVal) {
        this.refreshViewTrigger++;
      }
    },

    menuOpen(val, oldVal) {
      // menu just closed
      if (oldVal && !val && !this.multiple && this.isFocused) {
        this.$nextTick(() => {
          this.$refs.QPaginatedSearchDropdown?.blur()
        })
      }
    },
  },
};
</script>


<style lang="scss">
/* classes bellow fix a vuetify bug that breaks the persistant-placeholder when the input is unfocused */
.v-autocomplete:not(.v-input--is-focused).v-select--chips>>>input {
  max-height: inherit;
}

.v-autocomplete:not(.v-input--is-focused).v-select--chips input {
  max-height: inherit;
}

.intersect-point-line {
  content: '';
  width: 100%;
  height: 1px;
  position: absolute;

}

.intersect-point-line.visible {
  background-color: orange;
}

/* Single-select: while focused, hide rendered selection chip/text */
.q-input.focus-searching .v-select__selections .v-select__selection,
.q-input.focus-searching .v-select__selections .v-select__selection--comma {
  display: none !important;
}
/* Ensure slotted selection wrappers are also hidden when focusing (single-select) */
.q-input.focus-empty .v-select__selections .v-select__selection,
.q-input.focus-empty .v-select__selections .v-select__selection--comma {
  display: none !important;
}

/* keep layout/click area stable */
.q-input.focus-searching .v-select__selections {
  flex-wrap: nowrap;
}

.q-input.focus-empty .v-select__selections>*:not(input) {
  display: none !important;
}

/* Collapse the internal input in single-select mode so it doesn't take space */
.q-input:not(.focus-empty) .v-select__selections input {
  flex: 0 1 0px !important;
  width: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
}

/* Ensure the selection area can take full width */
.q-input .v-select__selections {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.q-input .v-select__selection {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

/* changed background to not have transparant scroll track when in overflow-y: overlay; */
.q-input .v-menu__content {
  background-color: rgb(248, 248, 248);
}

/* reduced padding on slection list menu items */
.q-input .v-menu__content .v-select-list .v-list-item {
  padding-right: 8px;
  padding-left: 12px;
}

/* Simple highlight for matched search term */
mark.qpsd-highlight {
  /* background-color: #fff3bf; */
  color: inherit;
  padding: 0;
}
</style>